import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiClient from '../api';

function phoneDigits(value) {
    return String(value || '').replace(/\D/g, '');
}

function formatPhoneInput(raw) {
    let digits = phoneDigits(raw);
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function phoneError(value) {
    if (!value) return '';
    const digits = phoneDigits(value);
    const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (normalized.length === 10) return '';
    return 'Use a 10-digit phone number, like (555) 555-5555';
}

function emailError(value) {
    const email = String(value || '').trim();
    if (!email) return '';
    if (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return '';
    return 'Enter a full email, like name@example.com';
}

const STAGE_COLOR = {
    notified: 'default',
    received: 'info',
    in_review: 'warning',
    approved: 'success',
    hired: 'success',
    retained: 'success',
    rejected: 'error',
};

function ReferralPage() {
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ candidateName: '', phone: '', email: '' });
    const [sent, setSent] = useState(null);
    const [tried, setTried] = useState(false);
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);

    const load = async () => {
        try {
            const res = await apiClient.get('/referrals');
            setRows(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load referrals');
        }
    };

    useEffect(() => { load(); }, []);

    const phoneProblem = phoneError(form.phone);
    const emailProblem = emailError(form.email);
    const missingContact = !form.phone && !String(form.email || '').trim();
    const canSend = form.candidateName.trim().length >= 2 && !phoneProblem && !emailProblem && !missingContact;

    const openForm = () => {
        setError('');
        setSent(null);
        setTried(false);
        setPhoneTouched(false);
        setEmailTouched(false);
        setForm({ candidateName: '', phone: '', email: '' });
        setOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setOpen(false);
        setSent(null);
    };

    const submit = async () => {
        setTried(true);
        if (!canSend) return;
        setSaving(true);
        setError('');
        try {
            const res = await apiClient.post('/referrals', {
                candidateName: form.candidateName.trim(),
                phone: form.phone,
                email: String(form.email || '').trim(),
            });
            const saved = res.data?.data;
            if (saved) setRows((prev) => [saved, ...prev.filter((row) => row._id !== saved._id)]);
            setSent({ name: form.candidateName.trim(), copySent: Boolean(res.data?.copySent) });
            setForm({ candidateName: '', phone: '', email: '' });
        } catch (err) {
            setError(err.response?.data?.message || 'Could not send the referral');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 720, pb: 2 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderTop: '4px solid #2e7d32' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <PaidIcon sx={{ color: '#2e7d32', fontSize: 36 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Referral program</Typography>
                </Stack>
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 800, mb: 0.5 }}>
                    $1,500
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    If the person you refer is hired and still here after 90 days.
                </Typography>
                <Button variant="contained" onClick={openForm} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
                    Refer someone
                </Button>
            </Paper>

            {error && !open && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack spacing={1}>
                {rows.length === 0 ? (
                    <Typography color="text.secondary">You have not sent a referral yet.</Typography>
                ) : rows.map((row) => (
                    <Paper key={row._id} variant="outlined" sx={{ px: 1.5, py: 1.25 }}>
                        <Stack spacing={0.75}>
                            <Typography sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{row.candidateName}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {[row.phone ? formatPhoneInput(row.phone) : '', row.email].filter(Boolean).join(' · ')}
                            </Typography>
                            {row.stage === 'hired' && row.daysLeft != null && (
                                <Typography variant="caption" color="text.secondary">
                                    {row.daysLeft === 0 ? '90 days complete' : `${row.daysLeft} days until the 90-day mark`}
                                </Typography>
                            )}
                            <Chip
                                label={row.stageLabel}
                                size="small"
                                color={STAGE_COLOR[row.stage] || 'default'}
                                sx={{ alignSelf: 'flex-start', fontWeight: 700, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                            />
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            <Dialog open={open} onClose={closeForm} fullWidth maxWidth="xs">
                {sent ? (
                    <>
                        <DialogTitle>Sent to the home office</DialogTitle>
                        <DialogContent>
                            <Stack spacing={1.5} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 40 }} />
                                <Typography>
                                    <strong>{sent.name}</strong> is on your referral list. The home office has the notice.
                                </Typography>
                                <Typography color="text.secondary">
                                    {sent.copySent
                                        ? 'A copy was emailed to you for your records.'
                                        : 'A personal copy was not emailed. The home office still has the referral.'}
                                </Typography>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="contained" onClick={closeForm} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>Done</Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle>Refer someone</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ color: '#2e7d32', fontWeight: 800, mb: 0.5 }}>$1,500 if hired and retained after 90 days</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                This goes to the home office, the same place as time off. You get a copy by email.
                            </Typography>
                            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                            <Stack spacing={1.5}>
                                <TextField
                                    label="Name"
                                    value={form.candidateName}
                                    onChange={(event) => setForm({ ...form, candidateName: event.target.value })}
                                    required
                                    autoFocus
                                    error={tried && form.candidateName.trim().length < 2}
                                    helperText={tried && form.candidateName.trim().length < 2 ? 'Enter their name' : ' '}
                                />
                                <TextField
                                    label="Phone"
                                    value={form.phone}
                                    onChange={(event) => setForm({ ...form, phone: formatPhoneInput(event.target.value) })}
                                    type="tel"
                                    autoComplete="tel"
                                    inputProps={{ inputMode: 'tel' }}
                                    placeholder="(555) 555-5555"
                                    onBlur={() => setPhoneTouched(true)}
                                    error={Boolean((phoneTouched || tried) && phoneProblem)}
                                    helperText={(phoneTouched || tried) && phoneProblem ? phoneProblem : 'Formats as you type'}
                                />
                                <TextField
                                    label="Email"
                                    value={form.email}
                                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                                    type="email"
                                    autoComplete="email"
                                    placeholder="name@example.com"
                                    onBlur={() => setEmailTouched(true)}
                                    error={Boolean((emailTouched || tried) && emailProblem)}
                                    helperText={(emailTouched || tried) && emailProblem ? emailProblem : ' '}
                                />
                                {tried && missingContact && (
                                    <Typography variant="caption" color="error">Enter a phone number or an email. Either one is enough.</Typography>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeForm} disabled={saving}>Cancel</Button>
                            <Button
                                variant="contained"
                                disabled={saving || (tried && !canSend)}
                                onClick={submit}
                                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                            >
                                {saving ? 'Sending…' : 'Send to home office'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}

export default ReferralPage;
